import React from "react";
import { connect } from "react-redux";
import { signIn, signOut, refreshToken } from "../actions/authActions";
import { checkStartSurvey } from "../actions/profileActions";
import history from "../history";

import Box from "@mui/material/Box";
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ListItemIcon from "@mui/material/ListItemIcon";
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import { Typography } from "@mui/material";

// how often to refresh tokens
const REFRESH_INTERVAL_MS = 14 * 60 * 1000; // 14 min (typical 15m token lifetime - a small safety margin)

class AuthCircle extends React.Component {
  state = { anchorEl: null };

  // internal guards
  refreshTimer = null;
  refreshing = false;
  lastRefreshAt = 0;

  componentDidMount() {
    // initial refresh on mount
    this.safeRefresh();
    // periodic refresh
    this.startRefreshLoop();

    // refresh when user returns to the tab / window
    window.addEventListener("focus", this.handleAppFocus);
    document.addEventListener("visibilitychange", this.handleAppFocus);

    this.props.checkStartSurvey();
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    window.removeEventListener("focus", this.handleAppFocus);
    document.removeEventListener("visibilitychange", this.handleAppFocus);
  }

  startRefreshLoop = () => {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(this.safeRefresh, REFRESH_INTERVAL_MS);
  };

  handleAppFocus = () => {
    // Always refresh at least once on first focus, and thereafter if we've been away long enough
    const now = Date.now();
    if (
      this.lastRefreshAt === 0 ||
      now - this.lastRefreshAt > REFRESH_INTERVAL_MS - 30 * 1000
    ) {
      this.safeRefresh();
    }
  };

  safeRefresh = async () => {
    if (this.refreshing) return;     // single-flight
    if (document.hidden) return;     // skip if tab not visible

    this.refreshing = true;
    try {
      await this.props.refreshToken(); // thunk must return/throw properly (see below)
    } catch {
      // optional: handle/log; redirect handled in thunk
    } finally {
      this.refreshing = false;
      this.lastRefreshAt = Date.now();
    }
  };

  handleMenu = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  onSignOutClick = () => {
    this.props.signOut();
    this.handleClose();
  };

  capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase();
  };

  render() {
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);

    return (
      <Box>
        <Tooltip title={`${this.props.firstname || ''} ${this.props.lastname || ''}`.trim()}>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={this.handleMenu}
            color="inherit"
            sx={{
              backgroundColor: 'primary.dark',
              padding: 1,
              borderRadius: '50%',
            }}
          >
            <Typography variant="body2">
              {this.capitalizeFirstLetter(this.props.firstname) + this.capitalizeFirstLetter(this.props.lastname)}
            </Typography>
          </IconButton>
        </Tooltip>

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={open}
          onClose={this.handleClose}
        >
          <MenuItem onClick={() => { history.push('/profile'); this.handleClose(); }}>
            <ListItemIcon><AccountCircle /></ListItemIcon>
            Profile
          </MenuItem>

          {this.props.hasAdminAccess && (
            <MenuItem onClick={() => { history.push('/users/admin'); this.handleClose(); }}>
              <ListItemIcon><ManageAccountsIcon /></ListItemIcon>
              User Admin
            </MenuItem>
          )}

          <MenuItem onClick={this.onSignOutClick}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    );
  }
}

const mapStateToProps = (state) => {
  const scopes = state.auth?.scopes || [];
  return {
    isSignedIn: state.auth?.isSignedIn,
    firstname: state.auth?.firstname,
    lastname: state.auth?.lastname,
    hasAdminAccess: scopes.includes('admin'),
  };
};

export default connect(
  mapStateToProps,
  { signIn, signOut, refreshToken, checkStartSurvey }
)(AuthCircle);